/**
 * Template List Component
 * Displays templates in a tabbed interface (System vs User templates)
 * with search, filter, and action capabilities
 *
 * @file src/components/email/templates/TemplateList.tsx
 * @created 2025-10-27
 */

import React, { useState, useMemo } from 'react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Search, Sparkles, User } from 'lucide-react';
import { EmailTemplate } from '@/api/email';
import TemplateGrid from './TemplateGrid';
import { Card, CardContent } from '@/components/ui/card';

interface TemplateListProps {
  templates: EmailTemplate[];
  onTemplateEdit: (template: EmailTemplate) => void;
  onTemplateDelete: (template: EmailTemplate) => void;  // Changed from templateId: string
  onTemplateDuplicate: (template: EmailTemplate) => void;
  onTemplatePreview?: (template: EmailTemplate) => void;
  onCapturePreview?: (template: EmailTemplate) => void;
  loading?: boolean;
  userRole?: string;
}

const TEMPLATE_CATEGORIES = [
  { value: 'all', label: 'All Categories' },
  { value: 'promotional', label: 'Promotional' },
  { value: 'newsletter', label: 'Newsletter' },
  { value: 'transactional', label: 'Transactional' },
  { value: 'trigger', label: 'Automated/Trigger' },
  { value: 'other', label: 'Other' },
];

/**
 * TemplateList Component
 *
 * Features:
 * - System vs User template tabs
 * - Search by template name
 * - Filter by category
 * - Template grid with action buttons
 * - Empty states
 *
 * @example
 * <TemplateList
 *   templates={templates}
 *   onTemplateEdit={(template) => handleEdit(template)}
 *   onTemplateDelete={(id) => handleDelete(id)}
 *   onTemplateDuplicate={(template) => handleDuplicate(template)}
 * />
 */
const TemplateList: React.FC<TemplateListProps> = ({
  templates,
  onTemplateEdit,
  onTemplateDelete,
  onTemplateDuplicate,
  onTemplatePreview,
  onCapturePreview,
  loading = false,
  userRole
}) => {
  const [activeTab, setActiveTab] = useState<'user' | 'system'>('user');
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');

  // Split templates into system and user
  const systemTemplates = useMemo(() => {
    return templates.filter(t => t.is_system_template);
  }, [templates]);

  const userTemplates = useMemo(() => {
    return templates.filter(t => !t.is_system_template);
  }, [templates]);

  // Apply search and filter
  const filterTemplates = (templateList: EmailTemplate[]) => {
    return templateList.filter(template => {
      // Search filter
      const matchesSearch = template.template_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (template.description?.toLowerCase().includes(searchTerm.toLowerCase()) || false);

      // Category filter
      const matchesCategory = categoryFilter === 'all' || template.category === categoryFilter;

      return matchesSearch && matchesCategory;
    });
  };

  const filteredUserTemplates = useMemo(() => filterTemplates(userTemplates), [userTemplates, searchTerm, categoryFilter]);
  const filteredSystemTemplates = useMemo(() => filterTemplates(systemTemplates), [systemTemplates, searchTerm, categoryFilter]);

  // Get the current template list based on active tab
  const currentTemplates = activeTab === 'user' ? filteredUserTemplates : filteredSystemTemplates;

  return (
    <div className="space-y-4">
      {/* Search and Filter Bar */}
      <div className="flex flex-col sm:flex-row gap-4">
        {/* Search */}
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search templates..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Category Filter */}
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-full sm:w-48">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            {TEMPLATE_CATEGORIES.map(cat => (
              <SelectItem key={cat.value} value={cat.value}>
                {cat.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-3xl font-bold text-primary">{currentTemplates.length}</p>
              <p className="text-sm text-muted-foreground">
                {activeTab === 'user' ? 'Your Templates' : 'System Templates'}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-3xl font-bold text-primary">{userTemplates.length}</p>
              <p className="text-sm text-muted-foreground">Total User Templates</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="text-center">
              <p className="text-3xl font-bold text-primary">{systemTemplates.length}</p>
              <p className="text-sm text-muted-foreground">System Templates Available</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs: User vs System Templates */}
      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'user' | 'system')}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="user" className="flex items-center gap-2 data-[state=active]:text-white group">
            <User className="h-4 w-4 group-data-[state=active]:text-white group-data-[state=active]:stroke-white" />
            My Templates ({userTemplates.length})
          </TabsTrigger>
          <TabsTrigger value="system" className="flex items-center gap-2 data-[state=active]:text-white group">
            <Sparkles className="h-4 w-4 group-data-[state=active]:text-white group-data-[state=active]:stroke-white" />
            System Templates ({systemTemplates.length})
          </TabsTrigger>
        </TabsList>

        {/* User Templates Tab */}
        <TabsContent value="user" className="mt-6">
          <TemplateGrid
            templates={filteredUserTemplates}
            onTemplateClick={onTemplateEdit}
            onTemplateDelete={onTemplateDelete}
            onTemplateDuplicate={onTemplateDuplicate}
            onTemplatePreview={onTemplatePreview}
            onCapturePreview={onCapturePreview}
            showCustomizeButton={false}
            userRole={userRole}
            emptyMessage={
              searchTerm || categoryFilter !== 'all'
                ? 'No templates match your search'
                : 'No user templates yet'
            }
          />
        </TabsContent>

        {/* System Templates Tab */}
        <TabsContent value="system" className="mt-6">
          <TemplateGrid
            templates={filteredSystemTemplates}
            onTemplateClick={onTemplateEdit}
            onTemplateDelete={onTemplateDelete}
            onTemplateDuplicate={onTemplateDuplicate}
            onTemplatePreview={onTemplatePreview}
            onCapturePreview={onCapturePreview}
            showCustomizeButton={true}
            userRole={userRole}
            emptyMessage={
              searchTerm || categoryFilter !== 'all'
                ? 'No templates match your search'
                : 'No system templates available'
            }
          />
        </TabsContent>
      </Tabs>

      {/* Clear Filters Hint */}
      {(searchTerm || categoryFilter !== 'all') && currentTemplates.length === 0 && (
        <div className="text-center py-8">
          <p className="text-sm text-muted-foreground mb-4">
            No templates found with current filters
          </p>
          <button
            onClick={() => {
              setSearchTerm('');
              setCategoryFilter('all');
            }}
            className="text-sm text-primary hover:underline"
          >
            Clear all filters
          </button>
        </div>
      )}
    </div>
  );
};

export default TemplateList;
